// EAGER MSSQL sniff walker (bundle-isolation: header reads only — no wkx/decoder import; plain
// DataView reads, matching sniffMysql.ts). MSSQL geometry/geography wire = MS-SSCLRT:
// [4B SRID LE][1B version][1B flags]…. `bytes` is the CAPPED read handed in by spatialSniff.ts's
// runWalkers (full value when under MAX_SNIFF_WALK_BYTES, else just HEADER_PREFIX_BYTES) — this file
// never materializes more than it is given. The single-point (P) and single-line (L) fast paths
// carry their type in the flags byte, so we report those cheaply; the general path's exact type
// lives in the TRAILING shapes array (not header-cheap), so we report `Unknown` (grid shows
// "GEOMETRY · …") and expose the O(1) numPoints as the point count only on a full (walk) read.
// Geography axis order stays out of the sniff — it is a decode concern (the T8 geography arm
// hard-throws per the c3 policy); the sniff is structural and never exposes coordinates.
import type { SpatialGeomType } from './types';
import { dimsLabel, type SniffFacts } from './sniffWalkers';

const F_Z = 0x01;
const F_M = 0x02;
const F_SINGLE_POINT = 0x08;
const F_SINGLE_LINE = 0x10;
const HEADER_BYTES = 6; // SRID(4) + version(1) + flags(1)

export function sniffMssqlClr(bytes: Uint8Array, walk: boolean, byteLength: number): SniffFacts | null {
  if (bytes.byteLength < HEADER_BYTES) return null;
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const version = dv.getUint8(4);
  if (version !== 1 && version !== 2) return null; // guards against non-spatial hex false positives
  const rawSrid = dv.getInt32(0, true); // signed: -1 = null geometry
  const flags = dv.getUint8(5);
  const dimensions = dimsLabel((flags & F_Z) !== 0, (flags & F_M) !== 0);
  const srid = rawSrid > 0 ? rawSrid : null; // SRID 0 or -1 (null) → null (engine parity)
  const base = { container: 'mssql-clr' as const, srid, dimensions, byteLength };

  // The single-Point (P) / single-Line (L) fast paths carry their coordinate doubles inline. A header
  // that claims one but has no room for the coordinates is an impossible body — fall through (null)
  // rather than report a phantom 1/2-point geometry. dpp = XY + optional Z + optional M.
  const dpp = 2 + ((flags & F_Z) !== 0 ? 1 : 0) + ((flags & F_M) !== 0 ? 1 : 0);
  if ((flags & F_SINGLE_POINT) !== 0) {
    if (bytes.byteLength < HEADER_BYTES + dpp * 8) return null;
    return { ...base, geomType: 'Point', pointCount: 1 };
  }
  if ((flags & F_SINGLE_LINE) !== 0) {
    if (bytes.byteLength < HEADER_BYTES + 2 * dpp * 8) return null;
    return { ...base, geomType: 'LineString', pointCount: 2 };
  }

  // General path: numPoints is the next uint32 (cheap); exact geomType is not header-cheap → Unknown.
  const pointCount = walk && bytes.byteLength >= HEADER_BYTES + 4 ? dv.getUint32(HEADER_BYTES, true) : null;
  return { ...base, geomType: 'Unknown', pointCount };
}

// Root shape type → GeoJSON type. Unlike the byte header (which only carries the P/L fast-path type),
// the fork-B marker carries the full shapes array, so the exact geomType is O(1)-derivable here.
const SHAPE_TO_GEOM: Record<number, SpatialGeomType> = {
  1: 'Point',
  2: 'LineString',
  3: 'Polygon',
  4: 'MultiPoint',
  5: 'MultiLineString',
  6: 'MultiPolygon',
  7: 'GeometryCollection',
};

type MarkerShape = { parentOffset: number; figureOffset: number; type: number };
type MarkerWire = {
  srid: number | null;
  version: number;
  kind: 'geometry' | 'geography';
  points: Array<{ x: number; y: number; z?: number }>;
  shapes: MarkerShape[];
};

/** EAGER sniff for the fork-B parsed marker `{ __jamMssqlGeometry }` (the driver pre-parsed the UDT,
 * so the value is an object, not hex — `spatialSniff.ts` dispatches this before the hexInfo() gate,
 * mirroring the Oracle SDO marker path). Facts are O(1): geomType from the root shape, dimensions
 * from point arity, srid + pointCount from the marker fields. Never walks coordinates. Header-only —
 * NO wkx/decoder import (bundle isolation). */
export function sniffMssqlMarker(value: unknown): SniffFacts | null {
  if (!value || typeof value !== 'object' || !('__jamMssqlGeometry' in value)) return null;
  const wire = (value as { __jamMssqlGeometry: MarkerWire }).__jamMssqlGeometry;
  if (!wire || typeof wire !== 'object' || !Array.isArray(wire.points) || !Array.isArray(wire.shapes)) {
    return null;
  }
  const root = wire.shapes.find((s) => s.parentOffset === -1);
  const geomType: SpatialGeomType | 'Unknown' = root ? SHAPE_TO_GEOM[root.type] ?? 'Unknown' : 'Unknown';
  const hasZ = wire.points.some((p) => typeof p.z === 'number');
  return {
    container: 'mssql-clr',
    geomType,
    srid: typeof wire.srid === 'number' && wire.srid > 0 ? wire.srid : null,
    dimensions: dimsLabel(hasZ, false),
    pointCount: wire.points.length,
    byteLength: wire.points.length * 16,
  };
}
